import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, Bell } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";

function apiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  }
  return fallback || "Something went wrong";
}

/**
 * Authenticated profile page. Today the only thing on it is the
 * change-password form — new users who were created by an admin will
 * land here on their first login to swap out the starter password.
 *
 * The current-password requirement is intentional: it prevents a stolen
 * session token from silently rotating the password and locking the
 * real user out.
 */
export default function ProfilePage() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmNext, setConfirmNext] = useState("");
  const [busy, setBusy] = useState(false);
  // Default the toggle to whatever the server sent (?? true covers
  // legacy user docs that don't have the flag yet).
  const [emailOn, setEmailOn] = useState(user?.receive_incident_emails ?? true);
  const [emailSaving, setEmailSaving] = useState(false);

  // Keep local state in sync when the auth context refreshes after a
  // successful PATCH — otherwise the useState initializer captures
  // the stale value from first render and the toggle can silently
  // diverge from the server state.
  useEffect(() => {
    setEmailOn(user?.receive_incident_emails ?? true);
  }, [user?.receive_incident_emails]);

  const toggleEmail = async (next) => {
    setEmailOn(next);
    setEmailSaving(true);
    try {
      await api.patch("/auth/me", { receive_incident_emails: next });
      await refresh?.();
      toast.success(next ? "Incident emails on" : "Incident emails off");
    } catch (err) {
      setEmailOn(!next); // revert on failure
      toast.error(apiError(err, "Could not update preference"));
    } finally {
      setEmailSaving(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirmNext) {
      toast.error("New passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      // Force a fresh login. The existing JWT keeps working until natural
      // expiry on the server side, but signing out + back in gives the
      // user a clean session and avoids the confusing state where their
      // tab still references "the old password" via the old token.
      toast.success("Password changed — please sign in again");
      setTimeout(() => {
        logout();
        navigate("/login", { replace: true });
      }, 1200);
    } catch (err) {
      toast.error(apiError(err, "Failed to change password"));
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs tracking-[0.3em] uppercase text-slate-400 font-display">Account</div>
        <h1
          className="font-display font-black text-3xl sm:text-4xl tracking-tight"
          data-testid="profile-heading"
        >
          Profile
        </h1>
      </div>

      <div className="grid gap-4 max-w-2xl">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-5 space-y-1">
            <div className="text-xs uppercase tracking-wider text-slate-400">Signed in as</div>
            <div className="text-lg font-display font-bold" data-testid="profile-name">
              {user?.full_name}
            </div>
            <div className="text-sm text-slate-400 font-mono" data-testid="profile-email">
              {user?.email}
            </div>
            <div className="text-xs uppercase tracking-wider text-amber-400 pt-2">
              Role: {user?.role}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800" data-testid="profile-notifications-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="w-4 h-4 text-amber-400" />
              <div className="font-display font-bold text-lg">Notification Preferences</div>
            </div>
            <div className="flex items-center justify-between gap-4 p-3 rounded-md border border-slate-800 bg-slate-950">
              <div className="min-w-0">
                <div className="text-sm font-display font-bold text-slate-100">Incident email notifications</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  Get an email when a new safety incident is filed that you&apos;re subscribed to,
                  or when a corrective action is assigned to you. In-app notifications aren&apos;t affected.
                </div>
              </div>
              <Switch
                checked={emailOn}
                onCheckedChange={toggleEmail}
                disabled={emailSaving}
                data-testid="profile-email-toggle"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <KeyRound className="w-4 h-4 text-amber-400" />
              <div className="font-display font-bold text-lg">Change Password</div>
            </div>
            <div className="text-xs text-slate-400 mb-4">
              Password must be at least 8 characters and include at least one letter and one number.
            </div>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label htmlFor="cur" className="text-xs uppercase tracking-wider text-slate-400">
                  Current password
                </Label>
                <Input
                  id="cur" type="password" required value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  className="bg-slate-950 border-slate-700 mt-1 font-mono"
                  data-testid="profile-current-pw"
                />
              </div>
              <div>
                <Label htmlFor="new" className="text-xs uppercase tracking-wider text-slate-400">
                  New password
                </Label>
                <Input
                  id="new" type="password" required minLength={8} value={next}
                  onChange={(e) => setNext(e.target.value)}
                  className="bg-slate-950 border-slate-700 mt-1 font-mono"
                  data-testid="profile-new-pw"
                />
              </div>
              <div>
                <Label htmlFor="conf" className="text-xs uppercase tracking-wider text-slate-400">
                  Confirm new password
                </Label>
                <Input
                  id="conf" type="password" required minLength={8} value={confirmNext}
                  onChange={(e) => setConfirmNext(e.target.value)}
                  className="bg-slate-950 border-slate-700 mt-1 font-mono"
                  data-testid="profile-confirm-pw"
                />
              </div>
              <Button
                type="submit" disabled={busy}
                className="bg-emerald-700 hover:bg-emerald-600 font-display tracking-wider"
                data-testid="profile-save-pw"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                UPDATE PASSWORD
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
